/**
 * Procedural per-save alphabet. The creatures invent their own writing —
 * every save seed derives a unique, stable 24-glyph script. Glyphs are
 * rasterized crisp on a 12x12 grid from a small stroke vocabulary
 * (line segments, boxes, dots, hooks); every glyph carries at least one
 * enclosing or crossing feature so the script reads as writing, not noise.
 * Deterministic: the same seed + index always renders the identical glyph.
 */

export const GLYPH_COUNT = 24;

const GRID = 12;

/**
 * imul-based string hash (splitmix-flavoured), kept local on purpose:
 * the UI layer never imports sim code. Shared by the other meta-layer
 * surfaces (profile portraits, logbook stamps).
 */
export function hashString(str: string): number {
  let h = 0x9e3779b9 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 0x85ebca6b);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return (h ^ (h >>> 15)) >>> 0;
}

/** mulberry32 — tiny deterministic PRNG over one 32-bit seed, yields [0,1). */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), s | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function setCell(g: Uint8Array, x: number, y: number): void {
  if (x >= 0 && x < GRID && y >= 0 && y < GRID) g[y * GRID + x] = 1;
}

/** Bresenham line rasterized onto the glyph grid. */
function line(g: Uint8Array, x0: number, y0: number, x1: number, y1: number): void {
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    setCell(g, x, y);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

function boxOutline(g: Uint8Array, x: number, y: number, w: number, h: number): void {
  line(g, x, y, x + w, y);
  line(g, x + w, y, x + w, y + h);
  line(g, x + w, y + h, x, y + h);
  line(g, x, y + h, x, y);
}

const clampG = (v: number): number => Math.max(0, Math.min(GRID - 1, v));

/** Fresh canvas element blitted from a cached master (one node, one home). */
function blitCopy(master: HTMLCanvasElement): HTMLCanvasElement {
  const copy = document.createElement('canvas');
  copy.width = master.width;
  copy.height = master.height;
  copy.style.cssText = master.style.cssText;
  const ctx = copy.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(master, 0, 0);
  }
  return copy;
}

export class GlyphAtlas {
  private readonly seed: string;
  private readonly grids = new Map<number, Uint8Array>();
  private readonly masters = new Map<string, HTMLCanvasElement>();

  constructor(seedString: string) {
    this.seed = seedString;
  }

  /**
   * 1:1 canvas of glyph #index (0..23): crisp pixelated strokes in `color`
   * on a transparent background, CSS-sized to `size` px. Rendering is cached
   * per (index, size, color); each call returns a fresh element blitted from
   * the cached master, since a canvas node can only live in one DOM spot.
   */
  canvas(index: number, size = 18, color = '#000'): HTMLCanvasElement {
    const idx = ((Math.floor(index) % GLYPH_COUNT) + GLYPH_COUNT) % GLYPH_COUNT;
    const key = `${idx}|${size}|${color}`;
    let master = this.masters.get(key);
    if (!master) {
      master = this.render(idx, size, color);
      this.masters.set(key, master);
    }
    return blitCopy(master);
  }

  /** Convenience: inline-block span wrapping the canvas, vertically centered. */
  span(index: number, size = 18, color = '#000'): HTMLSpanElement {
    const el = document.createElement('span');
    el.style.cssText = 'display:inline-block;vertical-align:middle;line-height:0;';
    el.appendChild(this.canvas(index, size, color));
    return el;
  }

  private grid(index: number): Uint8Array {
    let g = this.grids.get(index);
    if (!g) {
      g = this.buildGrid(index);
      this.grids.set(index, g);
    }
    return g;
  }

  /** 3-5 grid-snapped strokes; always one enclosing box or crossing pair. */
  private buildGrid(index: number): Uint8Array {
    const rand = mulberry32(hashString(`${this.seed}#glyph${index}`));
    const g = new Uint8Array(GRID * GRID);
    const ri = (lo: number, hi: number): number => lo + Math.floor(rand() * (hi - lo + 1));

    let extras: number;
    if (rand() < 0.5) {
      // enclosing feature: a small box (1 stroke)
      const w = ri(3, 6);
      const h = ri(3, 6);
      boxOutline(g, ri(1, GRID - 2 - w), ri(1, GRID - 2 - h), w, h);
      extras = ri(2, 4);
    } else {
      // crossing feature: intersecting horizontal + vertical (2 strokes)
      const cx = ri(3, GRID - 4);
      const cy = ri(3, GRID - 4);
      line(g, ri(1, 2), cy, ri(GRID - 3, GRID - 2), cy);
      line(g, cx, ri(1, 2), cx, ri(GRID - 3, GRID - 2));
      extras = ri(1, 3);
    }

    for (let i = 0; i < extras; i++) {
      switch (ri(0, 4)) {
        case 0: {
          // horizontal segment
          const y = ri(1, GRID - 2);
          const x0 = ri(1, GRID - 6);
          line(g, x0, y, clampG(x0 + ri(3, 6)), y);
          break;
        }
        case 1: {
          // vertical segment
          const x = ri(1, GRID - 2);
          const y0 = ri(1, GRID - 6);
          line(g, x, y0, x, clampG(y0 + ri(3, 6)));
          break;
        }
        case 2: {
          // diagonal segment
          const x0 = ri(1, GRID - 5);
          const y0 = ri(2, GRID - 3);
          const len = ri(3, 5);
          const dir = rand() < 0.5 ? 1 : -1;
          line(g, x0, y0, clampG(x0 + len), clampG(y0 + dir * len));
          break;
        }
        case 3:
          // dot
          setCell(g, ri(2, GRID - 3), ri(2, GRID - 3));
          break;
        default: {
          // hook: a vertical run with a short foot
          const x = ri(2, GRID - 4);
          const y = ri(1, GRID - 6);
          const len = ri(3, 5);
          const foot = ri(2, 3) * (rand() < 0.5 ? 1 : -1);
          line(g, x, y, x, clampG(y + len));
          line(g, x, clampG(y + len), clampG(x + foot), clampG(y + len));
          break;
        }
      }
    }
    return g;
  }

  private render(index: number, size: number, color: string): HTMLCanvasElement {
    // integer upscale of the 12x12 grid: at the default 18px this is 2px
    // per cell, i.e. 2px-wide strokes, nearest-neighbour crisp
    const cell = Math.max(1, Math.round(size / GRID));
    const canvas = document.createElement('canvas');
    canvas.width = GRID * cell;
    canvas.height = GRID * cell;
    canvas.style.cssText = `width:${size}px;height:${size}px;image-rendering:pixelated;display:block;`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = color;
    const g = this.grid(index);
    for (let y = 0; y < GRID; y++) {
      for (let x = 0; x < GRID; x++) {
        if (g[y * GRID + x]) ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    }
    return canvas;
  }
}
